import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Send, Eye, Calendar as CalendarIcon, ImageIcon, Check, Smartphone, Monitor, Moon, Search, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { upsertCampaign, getAudienceCount, sendTestEmail, scheduleCampaign, launchCampaign, getCampaign, listMarketingAudience, isSafeCtaUrl } from '@/lib/admin/marketing-campaigns.functions';
import { MediaField } from '@/components/admin/MediaField';
import { isValidAdminImageRef } from '@/lib/admin/media-url';
import { previewCampaign } from '@/lib/admin/marketing-preview.functions';
import { listMarketingDishes } from '@/lib/admin/marketing-dishes.functions';
import { listCoupons } from '@/lib/marketing/coupons.functions';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CAMPAIGN_DEFAULTS } from '@/lib/admin/marketing-defaults';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { z } from 'zod';

const searchSchema = z.object({
  id: z.string().uuid().optional(),
});

export const Route = createFileRoute('/_admin/admin/marketing/campaigns/new')({
  validateSearch: (search) => searchSchema.parse(search),
  component: NewCampaignPage,
});

const CAMPAIGN_TYPES = [
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'coupon_offer', label: 'Coupon / Offer' },
  { value: 'new_dish', label: 'New Dish' },
  { value: 'meal_plan_promotion', label: 'Meal Plan Promotion' },
  { value: 'reengagement', label: 'Re-engagement' },
  { value: 'seasonal', label: 'Festival / Seasonal' },
  { value: 'order_followup', label: 'Order Follow-up' },
  { value: 'custom', label: 'Custom' },
];

const AUDIENCE_TYPES = [
  { value: 'all_customers', label: 'All Customers (Opted-in)' },
  { value: 'active_customers', label: 'Active Customers' },
  { value: 'alacarte_customers', label: 'À La Carte Customers' },
  { value: 'meal_plan_customers', label: 'Meal Plan Customers' },
  { value: 'active_subscribers', label: 'Active Subscribers' },
  { value: 'selected_customers', label: 'Selected Customers' },
  { value: 'selected_emails', label: 'Selected Email Addresses' },
  { value: 'test', label: 'Test Email Only' },
];

const SITE_ORIGIN = 'https://poonamkapur.in';
// Destinations map to existing public routes only.
const CTA_DESTINATIONS = [
  { value: `${SITE_ORIGIN}/weekly-menu`, label: 'Weekly Menu' },
  { value: `${SITE_ORIGIN}/products`, label: 'Order Online (À la carte)' },
  { value: `${SITE_ORIGIN}/meal-plans`, label: 'Meal Plans' },
  { value: `${SITE_ORIGIN}/account`, label: 'My Account' },
];
const AUDIENCE_PAGE_SIZE = 25;

const EMAIL_TEMPLATES = [
  { value: 'marketing-campaign', label: 'Newsletter' },
  { value: 'marketing-campaign', label: 'Coupon Offer', id: 'coupon-offer' }, // UI label mapping
  { value: 'marketing-campaign', label: 'New Dish', id: 'new-dish' },
  { value: 'marketing-campaign', label: 'Meal Plan Promotion', id: 'meal-plan-promo' },
  { value: 'marketing-campaign', label: 'Order Follow-up', id: 'order-followup' },
  { value: 'marketing-campaign', label: 'Seasonal Offer', id: 'seasonal-offer' },
  { value: 'marketing-campaign', label: 'Custom', id: 'custom-marketing' },
];

function NewCampaignPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const [isTestSending, setIsTestSending] = useState(false);
  const [testEmails, setTestEmails] = useState('poonamkapur77@gmail.com\nhealthykitchen.testing@gmail.com\nnitin.chaurey12@gmail.com\nnitin.chaurey10@gmail.com');
  const [testResults, setTestResults] = useState<any[]>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
  const [isLaunching, setIsLaunching] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [time, setTime] = useState('09:00');
  const [customerSearch, setCustomerSearch] = useState('');
  const [audiencePage, setAudiencePage] = useState(1);
  const [dishSearch, setDishSearch] = useState('');
  const [ctaMode, setCtaMode] = useState<string>('custom');
  
  // Track if content was manually edited to avoid silent overwrites
  const isEditedRef = useRef({ subject: false, preview_text: false, content_html: false });
  const [pendingCampaignType, setPendingCampaignType] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLaunchConfirmOpen, setIsLaunchConfirmOpen] = useState(false);
  const [isImmediateLaunch, setIsImmediateLaunch] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'newsletter' as any,
    subject: CAMPAIGN_DEFAULTS.newsletter.subject,
    preview_text: CAMPAIGN_DEFAULTS.newsletter.preview_text,
    template_id: 'marketing-campaign',
    featured_image_url: '',
    content_html: CAMPAIGN_DEFAULTS.newsletter.content_html,
    cta_label: CAMPAIGN_DEFAULTS.newsletter.cta_label,
    cta_url: '',
    coupon_id: '',
    dish_ids: [] as string[],
    audience_type: 'test' as any,
    scheduled_at: null as string | null,
    audience_filter_json: {} as any
  });

  const { data: existingCampaign, isLoading: isCampaignLoading } = useQuery({
    queryKey: ['admin', 'marketing', 'campaigns', search.id],
    queryFn: () => search.id ? getCampaign({ data: { id: search.id } }) : null,
    enabled: !!search.id
  });

  useEffect(() => {
    if (existingCampaign) {
      setFormData({
        name: existingCampaign.name || '',
        type: existingCampaign.type || 'newsletter',
        subject: existingCampaign.subject || '',
        preview_text: existingCampaign.preview_text || '',
        template_id: existingCampaign.template_id || 'marketing-campaign',
        featured_image_url: existingCampaign.featured_image_url || '',
        content_html: existingCampaign.content_html || '',
        cta_label: existingCampaign.cta_label || '',
        cta_url: existingCampaign.cta_url || '',
        coupon_id: existingCampaign.coupon_id || '',
        dish_ids: existingCampaign.dish_ids || [],
        audience_type: existingCampaign.audience_type || 'test',
        scheduled_at: existingCampaign.scheduled_at,
        audience_filter_json: existingCampaign.audience_filter_json || {}
      });
      // Mark as edited to prevent default overwrites during initial load
      isEditedRef.current = { subject: true, preview_text: true, content_html: true };
      
      if (existingCampaign.scheduled_at) {
        const date = new Date(existingCampaign.scheduled_at);
        setSelectedDate(date);
        setTime(format(date, 'HH:mm'));
      }
    }
  }, [existingCampaign]);

  const applyCampaignDefaults = (type: string) => {
    const defaults = CAMPAIGN_DEFAULTS[type] || CAMPAIGN_DEFAULTS.custom;
    setFormData(prev => ({
      ...prev,
      type: type as any,
      subject: defaults.subject,
      preview_text: defaults.preview_text,
      content_html: defaults.content_html,
      cta_label: defaults.cta_label
    }));
    isEditedRef.current = { subject: false, preview_text: false, content_html: false };
  };

  const handleTypeChange = (newType: string) => {
    if (isEditedRef.current.subject || isEditedRef.current.preview_text || isEditedRef.current.content_html) {
      setPendingCampaignType(newType);
      setIsConfirmDialogOpen(true);
    } else {
      applyCampaignDefaults(newType);
    }
  };

  const confirmTypeChange = () => {
    if (pendingCampaignType) {
      applyCampaignDefaults(pendingCampaignType);
    }
    setIsConfirmDialogOpen(false);
    setPendingCampaignType(null);
  };

  const { data: coupons } = useQuery({ queryKey: ['admin', 'coupons'], queryFn: () => listCoupons() });
  const { data: dishes } = useQuery({ queryKey: ['admin', 'marketing', 'dishes'], queryFn: () => listMarketingDishes() });
  const { data: audience, isLoading: isAudienceLoading } = useQuery({
    queryKey: ['admin', 'marketing', 'audience'],
    queryFn: () => listMarketingAudience(),
    enabled: formData.audience_type === 'selected_customers',
  });
  const selectedCustomerIds: string[] = formData.audience_filter_json.selected_customer_ids || [];
  const setSelectedCustomerIds = (ids: string[]) =>
    setFormData((prev) => ({ ...prev, audience_filter_json: { ...prev.audience_filter_json, selected_customer_ids: ids } }));
  const filteredAudience = (audience?.eligible ?? []).filter((c) => {
    const q = customerSearch.trim().toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });
  const audiencePages = Math.max(1, Math.ceil(filteredAudience.length / AUDIENCE_PAGE_SIZE));
  const pageAudience = filteredAudience.slice((audiencePage - 1) * AUDIENCE_PAGE_SIZE, audiencePage * AUDIENCE_PAGE_SIZE);
  const filteredDishes = (dishes ?? []).filter((d) => !dishSearch.trim() || d.name.toLowerCase().includes(dishSearch.trim().toLowerCase()));
  const selectedDishes = (dishes ?? []).filter((d) => formData.dish_ids.includes(d.id));
  const featuredImageInvalid = !!formData.featured_image_url.trim() && !isValidAdminImageRef(formData.featured_image_url);
  const ctaUrlInvalid = !!formData.cta_url.trim() && !isSafeCtaUrl(formData.cta_url);

  useEffect(() => {
    const match = CTA_DESTINATIONS.find((d) => d.value === (existingCampaign?.cta_url ?? ''));
    setCtaMode(match ? match.value : 'custom');
  }, [existingCampaign]);

  const { data: audienceCountData, refetch: refetchCount } = useQuery({
    queryKey: ['admin', 'marketing', 'audience-count', formData.audience_type, formData.audience_filter_json],
    queryFn: async () => {
      return await getAudienceCount({ 
        data: {
          audience_type: formData.audience_type, 
          filter: formData.audience_filter_json 
        }
      });
    },
    enabled: !!formData.audience_type,
  });

  useEffect(() => { refetchCount(); }, [formData.audience_type, formData.audience_filter_json, refetchCount]);

  const handlePreview = async (silent = false) => {
    try {
      const cleaned = cleanFormData(formData);
      // Ensure we have at least subject and content before trying to preview
      if (!cleaned.subject || !cleaned.content_html) {
        if (!silent) toast.error('Subject and Content are required for preview');
        return;
      }
      
      const result = await previewCampaign({ campaignId: search.id || null, data: cleaned } as any);
      setPreviewHtml(result.html);
      if (!silent) setIsPreviewOpen(true);
    } catch (error: any) {
      console.error('Preview generation failed', error);
      if (!silent) toast.error('Preview generation failed: ' + (error.message || 'Unknown error'));
    }
  };

  // Update preview whenever form data changes and is relevant to the content
  useEffect(() => {
    // Only trigger auto-preview if subject and content are non-empty
    if (!formData.subject || !formData.content_html) return;

    const timer = setTimeout(() => {
      handlePreview(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    formData.subject, 
    formData.content_html, 
    formData.template_id, 
    formData.featured_image_url, 
    formData.cta_label, 
    formData.cta_url, 
    formData.coupon_id, 
    formData.dish_ids
  ]);

  const upsertMutation = useMutation({
    mutationFn: (data: any) => {
      const cleanedData = cleanFormData(data);
      const payload = { id: search.id || null, data: cleanedData };
      return upsertCampaign({ data: payload });
    },
    onSuccess: () => {
      toast.success('Campaign saved as draft');
      queryClient.invalidateQueries({ queryKey: ['admin', 'marketing', 'campaigns'] });
      navigate({ to: '/admin/marketing/campaigns' as any });
    },
    onError: (error: any) => {
      console.error("[CLIENT] upsertCampaign failed:", error);
      toast.error(error.message || 'Failed to save');
    },
  });

  const handleSave = () => {
    if (!formData.name || !formData.subject) {
      toast.error('Name and Subject are required');
      return;
    }
    if (featuredImageInvalid) {
      toast.error('Featured image must be chosen from the Media Library or be a full https:// URL');
      return;
    }
    if (ctaUrlInvalid) {
      toast.error('Button destination must be a full web address starting with https://');
      return;
    }
    upsertMutation.mutate(formData);
  };

  const validateDispatch = () => {
    if (!formData.name || !formData.subject || !formData.template_id || !formData.audience_type || !formData.content_html) {
      toast.error('Required fields: Name, Subject, Template, Content, Audience');
      return false;
    }
    if (audienceCountData?.count === 0) {
      toast.error('Audience cannot be empty');
      return false;
    }
    if (formData.type === 'coupon_offer' && !formData.coupon_id) {
      toast.error('Coupon is required for this campaign type');
      return false;
    }
    if (formData.type === 'new_dish' && (!formData.dish_ids || formData.dish_ids.length === 0)) {
      toast.error('At least one dish is required');
      return false;
    }
    return true;
  };

  const cleanFormData = (data: any) => {
    const cleaned = { ...data };
    if (cleaned.coupon_id === "") cleaned.coupon_id = null;
    if (Array.isArray(cleaned.dish_ids)) {
      cleaned.dish_ids = cleaned.dish_ids.filter((id: string) => id && id.length > 0);
    }
    if (Array.isArray(cleaned.meal_plan_ids)) {
      cleaned.meal_plan_ids = cleaned.meal_plan_ids.filter((id: string) => id && id.length > 0);
    }
    return cleaned;
  };

  const handleDispatch = async (immediate: boolean) => {
    if (!validateDispatch()) return;
    setIsImmediateLaunch(immediate);
    setIsLaunchConfirmOpen(true);
  };

  const confirmDispatch = async () => {
    setIsLaunchConfirmOpen(false);
    setIsLaunching(true);
    try {
      const cleaned = cleanFormData(formData);
      const campaign = await upsertCampaign({ data: { id: search.id || null, data: cleaned } });
      if (isImmediateLaunch) {
        const result = await launchCampaign({ data: { id: campaign.id } });
        toast.success(`Campaign launched. Recipients identified: ${result.recipientCount}`);
      } else {
        if (!formData.scheduled_at) {
          toast.error('Schedule date required');
          return;
        }
        await scheduleCampaign({ data: { id: campaign.id, scheduled_at: formData.scheduled_at } });
        toast.success('Campaign scheduled');
      }
      navigate({ to: '/admin/marketing/campaigns' as any });
    } catch (error: any) {
      toast.error(error.message || 'Dispatch failed', {
        description: error.message ? undefined : 'Please check your connection and try again.'
      });
    } finally {
      setIsLaunching(false);
    }
  };

  const handleSendTest = async () => {
    const emailList = testEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    const validEmails = emailList.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    const invalidEmails = emailList.filter(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

    if (validEmails.length === 0) {
      toast.error('No valid test email addresses provided');
      return;
    }

    if (invalidEmails.length > 0) {
      toast.warning(`Skipping ${invalidEmails.length} invalid email(s)`, {
        description: `Invalid: ${invalidEmails.join(', ')}`
      });
    }

    setIsTestSending(true);
    setTestResults([]);
    try {
      const cleaned = cleanFormData(formData);
      // Ensure we have a valid campaign ID before sending a test
      let campaignId = search.id;
      
      if (!campaignId) {
        // If not saved yet, save it now to get an ID
        const campaign = await upsertCampaign({ data: { id: null, data: cleaned } });
        campaignId = campaign.id;
      }
      
      const result = await sendTestEmail({ data: { emails: validEmails, campaignId: campaignId } } as any);
      
      setTestResults(result.results);
      
      const successCount = result.results.filter((r: any) => r.success).length;
      const failCount = result.results.length - successCount;

      if (successCount > 0) {
        toast.success(`Sent test email to ${successCount} recipient(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to send to ${failCount} recipient(s)`);
      }
    } catch (error: any) {
      toast.error('Failed to process test emails', {
        description: error.message || 'Please verify your Resend configuration.'
      });
    } finally {
      setIsTestSending(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-20">
      <div className="flex items-center justify-between sticky top-0 z-10 bg-background/80 backdrop-blur-md py-4 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">New Marketing Campaign</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSave} disabled={upsertMutation.isPending}>
            <Save className="mr-2 h-4 w-4" /> Save Draft
          </Button>
        </div>
      </div>

      {/* CAMPAIGN BASICS */}
      <Card>
        <CardHeader>
          <CardTitle>1. Campaign Basics</CardTitle>
          <CardDescription>Internal identification and campaign category.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Internal Campaign Name</Label>
              <Input placeholder="e.g. Monsoon Special Offer" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Campaign Type</Label>
              <Select value={formData.type} onValueChange={handleTypeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CAMPAIGN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TEMPLATE & CONTENT */}
      <Card>
        <CardHeader>
          <CardTitle>2. Template & Content</CardTitle>
          <CardDescription>Design the look and feel of your email.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Email Template</Label>
            <Select value={formData.template_id} onValueChange={(v) => setFormData({ ...formData, template_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EMAIL_TEMPLATES.map(t => <SelectItem key={t.id || t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          
          <Separator />

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input 
                placeholder="Grab 20% off on your next order!" 
                value={formData.subject} 
                onChange={(e) => {
                  setFormData({ ...formData, subject: e.target.value });
                  isEditedRef.current.subject = true;
                }} 
              />
            </div>
            <div className="space-y-2">
              <Label>Preview Text (Inbox Snippet)</Label>
              <Input 
                placeholder="Open to see what we have for you..." 
                value={formData.preview_text} 
                onChange={(e) => {
                  setFormData({ ...formData, preview_text: e.target.value });
                  isEditedRef.current.preview_text = true;
                }} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="featured-image">Featured Image</Label>
              <p className="text-xs text-muted-foreground">Choose an image from the Media Library, or paste a full https:// image link.</p>
              <MediaField
                id="featured-image"
                kind="image"
                allowUrlInput
                value={formData.featured_image_url}
                invalid={featuredImageInvalid}
                onChange={(v) => setFormData((prev) => ({ ...prev, featured_image_url: v }))}
              />
              {featuredImageInvalid && (
                <p className="text-xs text-destructive">Use a Media Library image or a full https:// URL.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Email Body Content (HTML/Markdown)</Label>
              <Textarea 
                className="min-h-[250px] font-mono text-sm" 
                placeholder="Write your email content here..." 
                value={formData.content_html} 
                onChange={(e) => {
                  setFormData({ ...formData, content_html: e.target.value });
                  isEditedRef.current.content_html = true;
                }} 
              />
              <div className="flex flex-wrap gap-2 pt-1">
                {['customer_name', 'coupon_code', 'discount', 'expiry_date', 'dish_name', 'meal_plan_name'].map(v => (
                  <Button 
                    key={v} 
                    variant="outline" 
                    size="sm" 
                    className="h-6 text-[10px] px-2"
                    onClick={() => {
                      const tag = `{{${v}}}`;
                      setFormData({ ...formData, content_html: formData.content_html + tag });
                      isEditedRef.current.content_html = true;
                    }}
                  >
                    +{v}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CTA Button Label</Label>
              <Input placeholder="Order Now" value={formData.cta_label} onChange={(e) => setFormData({ ...formData, cta_label: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta-destination">Where should this button take customers?</Label>
              <Select
                value={ctaMode}
                onValueChange={(v) => {
                  setCtaMode(v);
                  if (v !== 'custom') setFormData((prev) => ({ ...prev, cta_url: v }));
                }}
              >
                <SelectTrigger id="cta-destination" aria-label="Button destination"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CTA_DESTINATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  <SelectItem value="custom">Custom web address</SelectItem>
                </SelectContent>
              </Select>
              <Input
                aria-label="Button destination URL"
                placeholder="https://poonamkapur.in/weekly-menu"
                value={formData.cta_url}
                aria-invalid={ctaUrlInvalid}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({ ...prev, cta_url: v }));
                  setCtaMode(CTA_DESTINATIONS.find((d) => d.value === v)?.value ?? 'custom');
                }}
              />
              <p className="text-xs text-muted-foreground">This is the page customers open when they click the button in the email.</p>
              {ctaUrlInvalid && (
                <p className="text-xs text-destructive">Enter a full web address starting with https://</p>
              )}
            </div>
          </div>

          {formData.type === 'coupon_offer' && (
            <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <Label className="text-blue-700">Link Coupon</Label>
              <Select value={formData.coupon_id} onValueChange={(v) => setFormData({ ...formData, coupon_id: v })}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Select a coupon" /></SelectTrigger>
                <SelectContent>{coupons?.map(c => <SelectItem key={c.id} value={c.id}>{c.code} ({c.discount_type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {formData.type === 'new_dish' && (
            <div className="space-y-2 p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-green-700">Select Dish(es)</Label>
                <span className="text-xs font-medium" data-testid="dish-selected-count">{formData.dish_ids.length} selected</span>
              </div>
              <Input placeholder="Search dishes..." value={dishSearch} onChange={(e) => setDishSearch(e.target.value)} className="bg-white" />
              <div className="h-[220px] overflow-y-auto rounded border bg-white" data-testid="dish-list">
                {filteredDishes.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">No dishes match your search.</p>
                ) : filteredDishes.map(d => (
                  <label key={d.id} className="flex h-11 items-center gap-2 border-b px-3 text-sm cursor-pointer hover:bg-muted/40">
                    <input type="checkbox" checked={formData.dish_ids.includes(d.id)} onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => ({ ...prev, dish_ids: checked ? [...prev.dish_ids, d.id] : prev.dish_ids.filter(id => id !== d.id) }));
                    }} className="h-4 w-4 rounded border-gray-300" />
                    <span className="truncate">{d.name}</span>
                  </label>
                ))}
              </div>
              {selectedDishes.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {selectedDishes.map(d => (
                    <button key={d.id} type="button" className="rounded-full border bg-white px-2 py-0.5 text-xs" title="Remove"
                      onClick={() => setFormData(prev => ({ ...prev, dish_ids: prev.dish_ids.filter(id => id !== d.id) }))}>
                      {d.name} ×
                    </button>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setFormData(prev => ({ ...prev, dish_ids: [] }))}>Clear all</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AUDIENCE */}
      <Card>
        <CardHeader>
          <CardTitle>3. Audience</CardTitle>
          <CardDescription>Choose who will receive this campaign.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Target Audience Type</Label>
            <Select value={formData.audience_type} onValueChange={(v) => setFormData({ ...formData, audience_type: v as any })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{AUDIENCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {formData.audience_type === 'selected_customers' && (
            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex gap-2">
                <Search className="w-4 h-4 text-muted-foreground mt-3 ml-2" />
                <Input placeholder="Search customers by name or email..." value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); setAudiencePage(1); }} />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span data-testid="audience-summary">
                  {isAudienceLoading ? 'Loading customers…' : `${selectedCustomerIds.length} selected of ${audience?.eligible.length ?? 0} eligible customers`}
                </span>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                    disabled={!audience?.eligible.length}
                    onClick={() => setSelectedCustomerIds((audience?.eligible ?? []).map((c) => c.id))}>
                    Select all eligible customers
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" disabled={selectedCustomerIds.length === 0}
                    onClick={() => setSelectedCustomerIds([])}>
                    Clear selection
                  </Button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {pageAudience.length === 0 && !isAudienceLoading && (
                  <p className="p-2 text-sm text-muted-foreground">No eligible customers match your search.</p>
                )}
                {pageAudience.map(c => (
                  <label key={c.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded cursor-pointer border">
                    <input type="checkbox" checked={selectedCustomerIds.includes(c.id)} onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData(prev => {
                        const cur: string[] = prev.audience_filter_json.selected_customer_ids || [];
                        const ids = checked ? [...new Set([...cur, c.id])] : cur.filter((id) => id !== c.id);
                        return { ...prev, audience_filter_json: { ...prev.audience_filter_json, selected_customer_ids: ids } };
                      });
                    }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name || c.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                    </div>
                  </label>
                ))}
              </div>
              {audiencePages > 1 && (
                <div className="flex items-center justify-end gap-2 text-xs">
                  <Button type="button" variant="outline" size="sm" className="h-7" disabled={audiencePage <= 1} onClick={() => setAudiencePage((p) => p - 1)}>Prev</Button>
                  <span>Page {audiencePage} / {audiencePages}</span>
                  <Button type="button" variant="outline" size="sm" className="h-7" disabled={audiencePage >= audiencePages} onClick={() => setAudiencePage((p) => p + 1)}>Next</Button>
                </div>
              )}
              {audience && (
                <p className="text-xs text-muted-foreground" data-testid="audience-exclusions">
                  {audience.total} customers in total. Not shown: {audience.excluded.not_opted_in} not opted in to marketing, {audience.excluded.inactive} inactive/closed accounts{audience.excluded.no_email ? `, ${audience.excluded.no_email} without email` : ''}.
                </p>
              )}
            </div>
          )}

          {formData.audience_type === 'selected_emails' && (
            <div className="space-y-2">
              <Label>Enter Email Addresses (comma separated)</Label>
              <Textarea placeholder="john@example.com, jane@example.com" value={formData.audience_filter_json.emails || ''} onChange={(e) => setFormData({ ...formData, audience_filter_json: { ...formData.audience_filter_json, emails: e.target.value } })} />
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-muted rounded-md text-sm font-medium">
            Estimated Recipients: <span className="text-lg text-primary">{audienceCountData?.count || 0}</span>
          </div>
          {audienceCountData?.count === 0 && (
            <div className="flex items-center gap-2 text-sm text-destructive font-medium">
              <AlertCircle className="w-4 h-4" />
              Audience cannot be empty for dispatch.
            </div>
          )}
          <p className="text-xs text-muted-foreground">Note: All audiences automatically exclude customers who have not opted in to marketing (marketing_opt_in = true).</p>
        </CardContent>
      </Card>

      {/* PREVIEW */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>4. Preview</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreviewMode('desktop')} className={cn(previewMode === 'desktop' && 'bg-primary text-primary-foreground')}><Monitor className="w-4 h-4 mr-2" /> Desktop</Button>
              <Button variant="outline" size="sm" onClick={() => setPreviewMode('mobile')} className={cn(previewMode === 'mobile' && 'bg-primary text-primary-foreground')}><Smartphone className="w-4 h-4 mr-2" /> Mobile</Button>
              <Button variant="outline" size="sm" onClick={() => setPreviewTheme(previewTheme === 'light' ? 'dark' : 'light')}><Moon className="w-4 h-4 mr-2" /> Dark Mode</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg bg-gray-100 p-4 flex justify-center">
            <div className={cn(
              "bg-white transition-all shadow-xl overflow-hidden rounded-md",
              previewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-[800px]',
              previewTheme === 'dark' && 'invert hue-rotate-180'
            )}>
              <div className="h-[500px] overflow-y-auto">
                 {previewHtml ? (
                   <iframe srcDoc={previewHtml} className="w-full h-full border-0" title="Campaign Preview" />
                 ) : (
                   <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
                     <Eye className="w-12 h-12 opacity-20" />
                     <Button onClick={() => handlePreview(false)}>Generate Preview</Button>
                   </div>
                 )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SCHEDULE & DISPATCH */}
      <Card className="border-primary/20 shadow-lg shadow-primary/5">
        <CardHeader>
          <CardTitle>5. Schedule & Dispatch</CardTitle>
          <CardDescription>Launch your campaign now or schedule for later.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Dispatch Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" /> {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}</Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={selectedDate} onSelect={(d) => {
                  setSelectedDate(d);
                  if (d) {
                    const [h, m] = time.split(':');
                    const scheduled = new Date(d);
                    scheduled.setHours(parseInt(h), parseInt(m));
                    setFormData({ ...formData, scheduled_at: scheduled.toISOString() });
                  }
                }} disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))} /></PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Dispatch Time</Label>
              <Input type="time" value={time} onChange={(e) => {
                setTime(e.target.value);
                if (selectedDate) {
                  const [h, m] = e.target.value.split(':');
                  const scheduled = new Date(selectedDate);
                  scheduled.setHours(parseInt(h), parseInt(m));
                  setFormData({ ...formData, scheduled_at: scheduled.toISOString() });
                }
              }} />
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-3 pt-2">
             <Button className="flex-1 py-6 text-lg" disabled={isLaunching} onClick={() => handleDispatch(false)}>
               <CalendarIcon className="mr-2 h-5 w-5" /> Schedule Campaign
             </Button>
             <Button className="flex-1 py-6 text-lg" variant="secondary" disabled={isLaunching} onClick={() => handleDispatch(true)}>
               <Send className="mr-2 h-5 w-5" /> Send Immediately
             </Button>
          </div>
        </CardContent>
      </Card>

      {/* SEND TEST */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Send Test Email</CardTitle>
          <CardDescription>Send a verification email to reviewers before launching.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Test Email Addresses (One per line, or comma/semicolon separated)</Label>
              <Textarea 
                value={testEmails} 
                onChange={(e) => setTestEmails(e.target.value)} 
                placeholder="poonamkapur77@gmail.com&#10;healthykitchen.testing@gmail.com&#10;nitin.chaurey12@gmail.com"
                className="min-h-[100px]"
              />
              <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                <div>
                  Recipients selected: {testEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).length}
                </div>
                <div>
                  {(() => {
                    const list = testEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
                    const invalid = list.filter(e => e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
                    if (invalid.length > 0) {
                      return <span className="text-destructive font-medium">Invalid addresses: {invalid.length}</span>;
                    }
                    return <span>All addresses valid</span>;
                  })()}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleSendTest} 
                disabled={isTestSending}
                className="px-8"
              >
                {isTestSending ? 'Sending Tests...' : 'Send Test Emails'}
              </Button>
            </div>

            {testResults.length > 0 && (
              <div className="mt-6 border rounded-lg overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted text-muted-foreground font-medium border-b">
                    <tr>
                      <th className="p-3">Recipient</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Provider Message ID / Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {testResults.map((res, i) => (
                      <tr key={i}>
                        <td className="p-3">{res.email}</td>
                        <td className="p-3">
                          {res.success ? (
                            <span className="text-green-600 font-medium">Accepted</span>
                          ) : (
                            <span className="text-destructive font-medium">Failed</span>
                          )}
                        </td>
                        <td className="p-3 font-mono text-xs max-w-[300px] truncate">
                          {res.success ? res.messageId : (res.reason || res.error || 'Unknown Error')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Launch Confirmation Dialog */}
      <AlertDialog open={isLaunchConfirmOpen} onOpenChange={setIsLaunchConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isImmediateLaunch ? "Confirm Immediate Launch" : "Confirm Schedule"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 pt-2">
              <div className="space-y-4">
                {testResults.filter(r => r.success).length > 0 ? (
                  <p className="font-medium text-foreground">
                    Your test email has been sent to {testResults.filter(r => r.success).length} reviewers.
                  </p>
                ) : null}
                
                <p className="font-medium text-foreground">
                  Are you sure you want to send this campaign to the customer audience?
                </p>

                <div className="bg-muted p-3 rounded-md space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Target Audience:</span>
                    <span className="font-bold">{audienceCountData?.count || 0} customers</span>
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDispatch} disabled={isLaunching}>
              {isLaunching ? "Launching..." : "Continue to Send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Change Dialog */}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace with default template?</AlertDialogTitle>
            <AlertDialogDescription>
              You have customized this email. Replace it with the default template for this campaign type? This will overwrite your current changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsConfirmDialogOpen(false);
              setPendingCampaignType(null);
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmTypeChange}>Use Default Template</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
